/* eslint-disable class-methods-use-this */
const { orderType, sourceAndDestinationType, chargeCode, destinationChargeSlabUnit } = require('pando-constants');
const { freightUnit } = require('../../constants/payments');
const convertWeight = require('../../helpers/weightConverter');
const Helper = require('./utils/helper');
const convertVolume = require('../../helpers/volumeConverter');

class Oda {
  calculateWeight(unit, materialList) {
    const destinationWeight = materialList.map(material =>
      convertWeight(material.weightUnit, unit, material.weight))
      .reduce((acc, weight) => acc + weight, 0);
    const destinationVolume = materialList.map(material =>
      convertVolume(material.volumeUnit, 'cft', material.volume))
      .reduce((acc, volume) => acc + volume, 0);
    return { weight: destinationWeight, volume: destinationVolume };
  }

  calculateQuantity(materialList, unitType, materialMap, contractCharge,
    quantityUnitsForPieces) {
    if ((unitType !== freightUnit.per_trip) && (!materialList || materialList.length === 0)) {
      return 0;
    }
    const kg = 'kg';
    const tonne = 't';
    switch (unitType) {
      case freightUnit.per_trip:
        return contractCharge.odaRate;
      case freightUnit.per_kg: {
        const { weight, volume } = this.calculateWeight(kg, materialList);
        return Math.max(weight,
          volume * contractCharge.multiplyingFactor) * contractCharge.odaRate;
      }
      case freightUnit.per_package:
      case freightUnit.per_box:
      case freightUnit.per_case: {
        return materialList.map((material) => {
          const quantity = (material.quantity) ? Math.ceil(material.quantity) : 0;
          if (quantityUnitsForPieces[material.quantityUnit.toLowerCase()]) {
            const materialDetail = materialMap[material.materialCode];
            return (materialDetail.pce_box) ?
              Math.ceil(quantity / Math.ceil(materialDetail.pce_box)) : 0;
          }
          return quantity;
        }).reduce((acc, quantity) => acc + quantity, 0) * contractCharge.odaRate;
      }
      case freightUnit.per_ton: {
        const { weight } = this.calculateWeight(tonne, materialList);
        return weight * contractCharge.odaRate;
      }
      default:
        return 0;
    }
  }

  getSlabFactor(unit, materialList, quantityUnitsForPieces,
    materialMap, perKmDistance) {
    const slabUnit = destinationChargeSlabUnit();
    switch (unit.toUpperCase()) {
      case slabUnit.KM:
        return perKmDistance;
      case slabUnit.CFT: {
        return materialList.map(material =>
          convertVolume(material.volumeUnit, unit, material.volume))
          .reduce((acc, volume) => acc + volume, 0);
      }
      case slabUnit.KG: {
        const { weight } = this.calculateWeight(unit, materialList);
        return weight;
      }
      case slabUnit.BOX:
      case slabUnit.CASE: {
        return materialList.reduce((acc, material) => {
          const _acc = acc;
          const quantity = (material.quantity) ? Math.ceil(material.quantity) : 0;
          if (quantityUnitsForPieces[material.quantityUnit.toLowerCase()] && quantity) {
            const materialDetail = materialMap[material.materialCode];
            return (_acc + (materialDetail.pce_box ?
              Math.ceil(quantity / Math.ceil(materialDetail.pce_box)) : 0));
          }
          return _acc + quantity;
        }, 0);
      }
      default:
        return null;
    }
  }

  getDestinationContract(charges, materialList, quantityUnitsForPieces,
    materialMap, perKmDistance) {
    const [chargeObj] = charges;
    const isSlabBased = !!(chargeObj.slab.max);
    if (isSlabBased) {
      switch (chargeObj.charge_unit) {
        case freightUnit.per_trip:
        case freightUnit.per_kg: {
          const slabFactor = this.getSlabFactor(chargeObj.slab.unit.toLowerCase(), materialList,
            quantityUnitsForPieces, materialMap, perKmDistance);
          return charges.find(charge =>
            (slabFactor >= charge.slab.min && slabFactor < charge.slab.max));
        }
        default:
          return null;
      }
    }
    return chargeObj;
  }

  calculateOdaCharge(contractCharge, destination, data,
    clientConfig, destinationOdaCharges, quantityUnitsForPieces, gateId) {
    let odaCharge = 0;
    let odaDestinationCharge = 0;
    const perKmDistance = (destination.contract && destination.contract.per_km_distance) || 0;
    const destinationCharge = destinationOdaCharges && destinationOdaCharges.find(charge =>
      charge.destination_id.toString() === destination.locationId.toString());
    if (contractCharge) {
      const unitType = contractCharge.odaUnit;
      const destinationMaterials = Helper.getMaterialsForDestination(destination, gateId);
      const materialMap = Helper.fetchMaterialMap(data.materials.masterData);
      const materials = Helper
        .getMaterialsForDestinationBasedOnLooseMaterials(destinationMaterials, materialMap,
          clientConfig);
      odaCharge = Math.max(this.calculateQuantity(
        materials, unitType, materialMap,
        contractCharge, quantityUnitsForPieces), contractCharge.minimumOda);
      if (destinationCharge) {
        const charges = destinationCharge.charges;
        if (charges.length) {
          const contract = this.getDestinationContract(charges, materials,
            quantityUnitsForPieces, materialMap, perKmDistance);
          if (contract) {
            odaDestinationCharge = Math.max(this.calculateQuantity(
              materials, contract.charge_unit, materialMap,
              { odaRate: contract.charge, multiplyingFactor: contractCharge.multiplyingFactor },
              quantityUnitsForPieces),
            contract.min_charge);
          }
        }
      }
    }
    return (odaCharge + odaDestinationCharge);
  }

  getMaxChargeOfConsigneeGroup(_group, consigneeGroup, amount) {
    let charge = 0;
    const group = _group;
    if (!group[consigneeGroup]) {
      group[consigneeGroup] = 0;
    }
    if (amount > group[consigneeGroup]) {
      charge = (amount - group[consigneeGroup]);
      group[consigneeGroup] = amount;
    }
    return charge;
  }

  odaCharges(data, clientConfig = {}) {
    const freightContracts = data.freightCharges;
    const quantityUnitsForPieces = Helper
      .getQuantityUnitsForPieces(data.customQuantityUnits);
    if (data.orderType === orderType.CONTRACT) {
      // added filter since secondary consignees will not have gate id
      const gateIds = [...new Set(freightContracts.filter(contract => contract.sourceGateId)
        .map(contract => contract.sourceGateId.toString()))];
      const group = {};
      let odaCharge = 0;
      const destinationOdaCharges = data.indentDestinationCharges && data.indentDestinationCharges.filter(charge =>
        charge.charge_code === chargeCode.ODA);
      data.destinations.forEach((destination) => {
        // added condition here since secondary consignees will not have gate id
        if (gateIds.length) {
          gateIds.forEach((gateId) => {
            const contractCharge = freightContracts.find(freight =>
              (freight.sourceGateId.toString() === gateId) &&
            (freight.destinationId.toString() === destination.locationId.toString()));
            const amount = this.calculateOdaCharge(contractCharge, destination, data,
              clientConfig, destinationOdaCharges, quantityUnitsForPieces, gateId);
            odaCharge += (destination.type === sourceAndDestinationType.CONSIGNEE && destination.group && destination.group.length) ?
              this.getMaxChargeOfConsigneeGroup(group, destination.group, amount) : amount;
          });
        } else {
          const contractCharge = freightContracts.find(freight =>
            freight.destinationId.toString() === destination.locationId.toString());
          const amount = this.calculateOdaCharge(contractCharge, destination, data,
            clientConfig, destinationOdaCharges, quantityUnitsForPieces);
          odaCharge += (destination.type === sourceAndDestinationType.CONSIGNEE && destination.group && destination.group.length) ?
            this.getMaxChargeOfConsigneeGroup(group, destination.group, amount) : amount;
        }
      });
      return odaCharge;
    }
    return 0;
  }
}
module.exports = Oda;
